// How wide each bar is (issues 1174, 1179). Pure: the dock applies the plan, the tests grade it.
//
// An EMPTY bar is a slim strip holding its + alone, on every screen (the finding on #1064: at 390 px with every tool
// off the bars, the empty bars kept their 300 px and left the board a column 90 px wide); the + is the whole of it,
// and "Nothing here yet" is its name for a screen reader. A bar given a tool grows back to the width the screen gives
// a side bar. On a tablet the side bars are fitted to the screen so the board always keeps at least half its width.
import type { DeviceClass, SlotId } from '@dragons-table/vtt-shared';
import type { DockSlots } from './dockHandle';

/** An empty bar's strip: a finger's 44 px on a coarse pointer, a little less under a mouse. */
export const EMPTY_STRIP_PX = { fine: 36, coarse: 44 } as const;
/** The smallest touch target the tablet layout allows (WCAG 2.5.5's 44 × 44 CSS px). */
export const TOUCH_MIN_PX = 44;

export const STOCK_SIDE_WIDTH = 300;
/**
 * The stock side bars' width at this screen's text size (issue 1174): half again as wide at 200%, so a doubled panel
 * still carries a line of words without taking the board (1280 px wide leaves the board about 380 px at 200%).
 */
export function stockSideWidth(scale = rootTextScale()): number {
  return Math.round(STOCK_SIDE_WIDTH * (1 + (Math.max(1, Math.min(2, scale)) - 1) / 2));
}
/** The root font size over the browser's 16 px: the person's text size as the shell applied it. */
export function rootTextScale(): number {
  const px = typeof document === 'undefined' ? NaN : parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(px) && px > 0 ? px / 16 : 1;
}

/** The share of the window's width the board keeps with both side bars held, on a tablet or a desktop, whatever the text size (issue 1467). */
export const BOARD_MIN_WIDTH_SHARE = 0.5;
/** Each of a tablet's side bars takes at most this share of the width (two of them leave the board 60%). */
export const TABLET_SIDE_SHARE = 0.2;
/** A lone side bar (the other empty) at most: the board keeps the rest, less the empty bar's strip. */
export const DESKTOP_SIDE_SHARE = 0.4;

/** A tablet's side bar: the desktop's width where the screen has room, else a fifth of the width, never under a finger's 44 px. */
export function tabletSideWidth(screenWidth: number, scale = rootTextScale()): number {
  return Math.max(TOUCH_MIN_PX, Math.min(stockSideWidth(scale), Math.floor(screenWidth * TABLET_SIDE_SHARE)));
}

export interface BarSize {
  empty: boolean;
  px: number;
  /** A held side bar's narrowest (issue 1468): grows with the text size, never past `max`. */
  min?: number;
  /** A held side bar's widest (issue 1467): what leaves the board its share; a saved or dragged width is held under it. */
  max?: number;
}
export type BarPlan = Record<Exclude<SlotId, 'center'>, BarSize>;

/** The narrowest a held side bar is at 100% text: a tab title such as "Turn tracker" reads whole beside its header's controls. */
export const SIDE_TITLE_PX = 200;

/**
 * A held side bar's narrowest at this text size (issue 1468): 200 px at 100%, where a tab's title reads whole (the
 * retest on #1467: a window narrowed from full width, or reloaded narrow, has its bars scaled down by dockview, and a
 * 150 px floor left them at 152 px reading "Turn tr…"), and half the stock width with the text's growth twice over
 * above it, so at 200% it is the stock 450 — a bar dragged or saved narrow at 100% widens with the text instead of
 * wrapping a word a line. Always under the bar's `max`, which wins.
 */
export function sideFloor(scale = rootTextScale()): number {
  return Math.max(SIDE_TITLE_PX, Math.round((stockSideWidth(scale) * Math.max(1, Math.min(2, scale))) / 2));
}

/**
 * The widest a held side bar may be (issue 1467): with both side bars held, each takes at most half of what the board's
 * share leaves (a quarter of the width each, the board keeping half); with one, the 40% a lone bar always had. An 800 px
 * desktop window once kept two 300 px bars (or a saved arrangement's) and left the board a 200–300 px column.
 */
export function sideCap(width: number, held: number, device: DeviceClass): number {
  if (held >= 2) return Math.floor((width * (1 - BOARD_MIN_WIDTH_SHARE)) / 2);
  return Math.floor(width * (device === 'tablet' ? TABLET_SIDE_SHARE * 2 : DESKTOP_SIDE_SHARE));
}

/**
 * Each bar's size for what it holds. `px` is a side bar's width, or for the bottom bar its height when empty (a held
 * bottom bar is fitted to the initiative strip, issue 1070, and its `px` is only where a load starts it). A held side
 * bar also carries `min` and `max`: the dock holds it between them however it was dragged or saved, and refits it when
 * the window or the text size changes, so the board keeps its share (issues 1467, 1468).
 */
export function barPlan(slots: DockSlots, { device, width, coarse, scale }: { device: DeviceClass; width: number; coarse: boolean; scale: number }): BarPlan {
  const strip = coarse ? EMPTY_STRIP_PX.coarse : EMPTY_STRIP_PX.fine;
  const held = (slots.left === null ? 0 : 1) + (slots.right === null ? 0 : 1);
  const cap = Math.max(TOUCH_MIN_PX, sideCap(width, held, device));
  // A desktop's side bar: the stock width at this text size, within what leaves the board its share (a 390 px window
  // once gave a bar 300 px and the board 92; an 800 px one gave two bars 300 px and the board 200).
  const side = device === 'tablet' ? tabletSideWidth(width, scale) : Math.max(TOUCH_MIN_PX, Math.min(stockSideWidth(scale), cap));
  const max = Math.max(side, cap);
  const min = device === 'tablet' ? Math.min(100, side) : Math.min(max, Math.max(TOUCH_MIN_PX, sideFloor(scale)));
  const size = (slot: 'left' | 'right' | 'bottom'): BarSize => {
    if (slots[slot] === null) return { empty: true, px: strip };
    return slot === 'bottom' ? { empty: false, px: STOCK_BOTTOM_HEIGHT } : { empty: false, px: side, min, max };
  };
  return { left: size('left'), right: size('right'), bottom: size('bottom') };
}
export const STOCK_BOTTOM_HEIGHT = 112;

/** An opened side bar in the phone layout (issue 1242): 320 px, or what leaves a sliver of the board in sight. */
export function phoneBarWidth(width: number): number {
  return Math.min(320, Math.round(width * 0.86));
}

/**
 * Where a tool opened from a menu slides in over the board on a tablet (issue 1179): from the right on a landscape
 * screen, 45% of its width; up from the bottom on a portrait one, half its height. The board behind keeps the rest,
 * and the sheet closes with one tap.
 */
export function sheetPlacement(width: number, height: number): { edge: 'right' | 'bottom'; px: number } {
  return width > height ? { edge: 'right', px: Math.round(width * 0.45) } : { edge: 'bottom', px: Math.round(height * 0.5) };
}
